/**
 * useAISalesChat Hook
 *
 * Provides React Query hooks for AI Sales Chat functionality:
 * - Send messages to AI assistant
 * - Load conversation messages
 * - Optimistic updates for instant UI feedback
 *
 * @example
 * ```tsx
 * const { mutate: sendMessage, isPending } = useSendMessage();
 * const { data: messages, isLoading } = useConversationMessages(conversationId);
 *
 * sendMessage({
 *   conversationId: 'conv123',
 *   message: 'Research Tesla',
 * });
 * ```
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { collection, query, where, getDocs, orderBy, Timestamp, doc, getDoc } from 'firebase/firestore';
import { getFunctions } from 'firebase/functions';
import { httpsCallable } from '../../lib/firebase/authReadyCallable';
import { agentDeliveryRefetchInterval, closeStalePlaceholders } from '../../lib/crm/agentDeliveryRefetch';
import { db, functions } from '../../config/firebase';
import { COLLECTIONS } from '../../config/collections';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import * as Sentry from '@sentry/react';
import { toMillisSafe } from '../../lib/utils/timestamp';
import type { Conversation, ConversationMessage } from '../../types/ai-sales-chat';
import { logCallableFailure } from '../../lib/logCallableFailure';

/**
 * Callable responses are JSON: an Admin SDK Timestamp arrives as a plain
 * `{_seconds, _nanoseconds}` object with no `toDate`/`toMillis`. Rebuild a real
 * client Timestamp so consumers can rely on the ConversationMessage type.
 * A time that cannot be decoded is NOT replaced with "now" (that would show a made-up time):
 * the message is returned as received, and the panel shows no time for it and sorts it last.
 */
export function reviveCallableMessage(message: ConversationMessage | undefined): ConversationMessage | undefined {
  if (!message) return undefined;
  const raw = (message as { createdAt?: unknown }).createdAt;
  const millis = toMillisSafe(raw);
  if (millis === null) {
    Sentry.addBreadcrumb({
      category: 'ai-chat',
      level: 'warning',
      message: 'aiSalesChat_sendMessage returned an undecodable createdAt',
      data: { messageId: message.messageId, createdAt: raw },
    });
    return message;
  }
  try {
    return { ...message, createdAt: Timestamp.fromMillis(millis) };
  } catch {
    // A finite time outside Timestamp's range (before year 1 / after 9999) throws; the send itself succeeded, so
    // return the message as received rather than turning a sent message into an error.
    return message;
  }
}

// Query key factory
export const conversationKeys = {
  all: ['conversations'] as const,
  lists: () => [...conversationKeys.all, 'list'] as const,
  list: (filters?: { isArchived?: boolean }) => [...conversationKeys.lists(), filters] as const,
  details: () => [...conversationKeys.all, 'detail'] as const,
  detail: (id: string) => [...conversationKeys.details(), id] as const,
  messages: (conversationId: string) => [...conversationKeys.all, 'messages', conversationId] as const,
};

// ============================================================================
// CONVERSATION QUERIES
// ============================================================================

/**
 * Fetch all conversations for the current user
 *
 * @example
 * ```tsx
 * const { data: conversations, isLoading } = useConversations();
 *
 * // With filters
 * const { data: active } = useConversations({ isArchived: false });
 * ```
 */
export function useConversations(filters?: { isArchived?: boolean }) {
  const { currentOrganization } = useOrganization();
  const { firebaseUser } = useAuth();

  return useQuery({
    queryKey: conversationKeys.list(filters),
    queryFn: async (): Promise<Conversation[]> => {
      if (!currentOrganization?.organizationId || !firebaseUser?.uid) {
        return [];
      }

      const constraints = [
        where('organizationId', '==', currentOrganization.organizationId),
        where('userId', '==', firebaseUser.uid),
        orderBy('lastMessageAt', 'desc'),
      ];

      const q = query(collection(db, COLLECTIONS.CONVERSATIONS), ...constraints);
      const snapshot = await getDocs(q);

      let conversations = snapshot.docs.map((doc) => ({
        ...doc.data(),
        conversationId: doc.id,
      })) as Conversation[];

      // Client-side filter for archived
      if (filters?.isArchived !== undefined) {
        conversations = conversations.filter((c) => c.isArchived === filters.isArchived);
      }

      return conversations;
    },
    enabled: !!currentOrganization?.organizationId && !!firebaseUser?.uid,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

/**
 * Fetch a single conversation by ID
 */
export function useConversation(conversationId?: string) {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: conversationKeys.detail(conversationId || ''),
    queryFn: async (): Promise<Conversation | null> => {
      if (!conversationId) return null;

      const docRef = doc(db, COLLECTIONS.CONVERSATIONS, conversationId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) return null;

      return {
        ...docSnap.data(),
        conversationId: docSnap.id,
      } as Conversation;
    },
    enabled: !!conversationId,
    staleTime: 1000 * 60 * 5,
    initialData: () => {
      const lists = queryClient.getQueriesData<Conversation[]>({ queryKey: conversationKeys.lists() });
      for (const [, items] of lists) {
        if (!Array.isArray(items)) continue;
        const found = items?.find((c) => c.conversationId === conversationId);
        if (found) return found;
      }
      return undefined;
    },
    initialDataUpdatedAt: 0, // seed is treated as stale so the full record still refetches immediately
  });
}

// ============================================================================
// CONVERSATION MESSAGES QUERY
// ============================================================================

/**
 * Fetch messages for a conversation
 *
 * @example
 * ```tsx
 * const { data: messages, isLoading } = useConversationMessages(conversationId);
 *
 * return (
 *   <div>
 *     {messages?.map(msg => (
 *       <MessageBubble key={msg.messageId} message={msg} />
 *     ))}
 *   </div>
 * );
 * ```
 */
export function useConversationMessages(conversationId?: string) {
  return useQuery({
    queryKey: conversationKeys.messages(conversationId || ''),
    queryFn: async (): Promise<ConversationMessage[]> => {
      if (!conversationId) return [];

      const q = query(
        collection(db, COLLECTIONS.CONVERSATIONS, conversationId, 'messages'),
        orderBy('createdAt', 'asc')
      );

      const snapshot = await getDocs(q);
      // ZEUS-P0-2: a hand-off placeholder still open past the polling cap is shown as not finished.
      return closeStalePlaceholders(snapshot.docs.map((doc) => ({
        ...doc.data(),
        messageId: doc.id,
      })) as ConversationMessage[]);
    },
    enabled: !!conversationId,
    staleTime: 1000 * 60 * 2,
    // ZEUS-P0-2: an agent hand-off answers into the conversation later; poll until it lands (capped).
    refetchInterval: (q) => agentDeliveryRefetchInterval(q.state.data),
  });
}

// ============================================================================
// MUTATIONS
// ============================================================================

interface SendMessageParams {
  conversationId?: string; // Omit to create new conversation
  message: string;
  context?: {
    leadId?: string;
    leadName?: string;
    pipelineId?: string;
    pipelineName?: string;
    /** Current route path (e.g. /crm/leads/abc123) */
    currentRoute?: string;
    /** Previous route path for navigation context */
    previousRoute?: string;
    /** Current module name (e.g. sales, support, projects) */
    currentModule?: string;
  };
}

interface SendMessageResponse {
  conversationId: string;
  /** undefined when the server sent no message (callers already check both before using them). */
  userMessage: ConversationMessage | undefined;
  assistantMessage: ConversationMessage | undefined;
  /** Route path for navigation — response-only, not persisted */
  navigateTo?: string;
}

/**
 * Send a message to the AI assistant
 *
 * Handles both new conversations and existing ones. Uses optimistic updates
 * for instant UI feedback while waiting for AI response.
 *
 * @example
 * ```tsx
 * const { mutate: sendMessage, isPending } = useSendMessage();
 *
 * const handleSend = () => {
 *   sendMessage({
 *     conversationId: 'conv123', // Omit to create new conversation
 *     message: 'Research Tesla',
 *     context: {
 *       leadId: 'lead123',
 *       leadName: 'Tesla Inc',
 *     },
 *   });
 * };
 * ```
 */
export function useSendMessage() {
  const queryClient = useQueryClient();
  const { currentOrganization } = useOrganization();
  const { firebaseUser } = useAuth();

  return useMutation({
    mutationFn: async ({
      conversationId,
      message,
      context,
    }: SendMessageParams): Promise<SendMessageResponse> => {
      if (!currentOrganization?.organizationId) {
        throw new Error('No organization selected');
      }
      if (!firebaseUser?.uid) {
        throw new Error('Not authenticated');
      }

      const functions = getFunctions();
      const sendChatMessageFn = httpsCallable<
        {
          organizationId: string;
          userId: string;
          conversationId?: string;
          message: string;
          context?: SendMessageParams['context'];
        },
        {
          success: boolean;
          conversationId: string;
          userMessage: ConversationMessage;
          assistantMessage: ConversationMessage;
        }
      >(functions, 'aiSalesChat_sendMessage');

      const result = await sendChatMessageFn({
        organizationId: currentOrganization.organizationId,
        userId: firebaseUser.uid,
        conversationId,
        message,
        context,
      });

      if (!result.data.success) {
        throw new Error('Failed to send message');
      }

      return {
        conversationId: result.data.conversationId,
        userMessage: reviveCallableMessage(result.data.userMessage),
        assistantMessage: reviveCallableMessage(result.data.assistantMessage),
        navigateTo: (result.data as any).navigateTo,
      };
    },
    onMutate: async ({ conversationId, message }) => {
      if (!conversationId) {
        // New conversation - can't optimistically update yet
        return;
      }

      // Cancel in-flight queries
      await queryClient.cancelQueries({ queryKey: conversationKeys.messages(conversationId) });

      // Snapshot previous messages
      const previousMessages = queryClient.getQueryData<ConversationMessage[]>(
        conversationKeys.messages(conversationId)
      );

      const now = Timestamp.now();

      // Optimistically add user message
      const optimisticUserMessage: ConversationMessage = {
        messageId: `temp-user-${Date.now()}`,
        conversationId,
        role: 'user',
        content: message,
        createdAt: now,
      };

      queryClient.setQueryData<ConversationMessage[]>(
        conversationKeys.messages(conversationId),
        (old) => {
          if (!old) return [optimisticUserMessage];
          return [...old, optimisticUserMessage];
        }
      );

      return { previousMessages, conversationId };
    },
    onSuccess: (data, variables) => {
      // Invalidate conversation list (updates lastMessageAt, messageCount)
      queryClient.invalidateQueries({ queryKey: conversationKeys.lists() });

      // Invalidate messages to get real message IDs
      queryClient.invalidateQueries({ queryKey: conversationKeys.messages(data.conversationId) });

      // If new conversation was created, also invalidate detail
      if (!variables.conversationId) {
        queryClient.invalidateQueries({ queryKey: conversationKeys.detail(data.conversationId) });
      }
    },
    onError: (error: Error, variables, context) => {
      logCallableFailure('Failed to send message:', error);

      // Rollback optimistic update
      if (context?.conversationId && context?.previousMessages) {
        queryClient.setQueryData(
          conversationKeys.messages(context.conversationId),
          context.previousMessages
        );
      }

      // Show user-friendly error
      let message = 'Failed to send message';
      if (error.message.includes('API key')) {
        message = 'AI assistant not configured. Contact administrator.';
      } else if (error.message) {
        message = error.message;
      }

      toast.error(message);
    },
  });
}

/**
 * Execute an action from an AI message (e.g., "Research Lead" button)
 *
 * @example
 * ```tsx
 * const { mutate: executeAction } = useExecuteAction();
 *
 * executeAction({
 *   conversationId: 'conv123',
 *   messageId: 'msg456',
 *   actionIndex: 0,
 * });
 * ```
 */
export function useExecuteAction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      conversationId,
      messageId,
      actionIndex,
    }: {
      conversationId: string;
      messageId: string;
      actionIndex: number;
    }): Promise<void> => {
      const functions = getFunctions();
      const executeActionFn = httpsCallable<
        { conversationId: string; messageId: string; actionIndex: number },
        { success: boolean; result: any }
      >(functions, 'aiSalesChat_executeAction');

      const result = await executeActionFn({ conversationId, messageId, actionIndex });

      if (!result.data.success) {
        throw new Error('Failed to execute action');
      }
    },
    onSuccess: (_, variables) => {
      // Invalidate messages to get updated action.executed status
      queryClient.invalidateQueries({ queryKey: conversationKeys.messages(variables.conversationId) });

      toast.success('Action executed');
    },
    onError: (error: Error) => {
      logCallableFailure('Failed to execute action:', error);
      toast.error('Failed to execute action');
    },
  });
}

/**
 * Archive a conversation
 */
export function useArchiveConversation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (conversationId: string): Promise<void> => {
      const callable = httpsCallable(functions, 'crm_dispatch');
      await callable({ action: 'archiveConversation', conversationId });
    },
    onMutate: async (conversationId) => {
      await queryClient.cancelQueries({ queryKey: conversationKeys.lists() });
      const previousLists = queryClient.getQueriesData<Conversation[]>({
        queryKey: conversationKeys.lists(),
      });
      queryClient.setQueriesData<Conversation[]>(
        { queryKey: conversationKeys.lists() },
        (old) => {
          if (!old || !Array.isArray(old)) return old;
          return old.map((c) =>
            c.conversationId === conversationId ? { ...c, isArchived: true } : c
          );
        }
      );
      return { previousLists };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousLists) {
        for (const [queryKey, data] of context.previousLists) {
          queryClient.setQueryData(queryKey, data);
        }
      }
      toast.error('Failed to archive conversation');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: conversationKeys.lists() });
    },
    onSuccess: () => {
      toast.success('Conversation archived');
    },
  });
}

/**
 * Unarchive a conversation
 */
export function useUnarchiveConversation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (conversationId: string): Promise<void> => {
      const callable = httpsCallable(functions, 'crm_dispatch');
      await callable({ action: 'unarchiveConversation', conversationId });
    },
    onMutate: async (conversationId) => {
      await queryClient.cancelQueries({ queryKey: conversationKeys.lists() });
      const previousLists = queryClient.getQueriesData<Conversation[]>({
        queryKey: conversationKeys.lists(),
      });
      queryClient.setQueriesData<Conversation[]>(
        { queryKey: conversationKeys.lists() },
        (old) => {
          if (!old || !Array.isArray(old)) return old;
          return old.map((c) =>
            c.conversationId === conversationId ? { ...c, isArchived: false } : c
          );
        }
      );
      return { previousLists };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousLists) {
        for (const [queryKey, data] of context.previousLists) {
          queryClient.setQueryData(queryKey, data);
        }
      }
      toast.error('Failed to restore conversation');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: conversationKeys.lists() });
    },
    onSuccess: () => {
      toast.success('Conversation restored');
    },
  });
}
